import { ghostBtnClass, sectionCardClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  ownerQcChecklistDecision,
  requestQcChecklistArchive,
  setQcCategoryRequired,
  type QcChecklistCategory,
  type QcChecklistItem,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import { renameQcChecklistCategory, reorderQcChecklist } from "@/lib/manufacturing/qcChecklistArrangeClient";
import { moveChecklistId } from "@/lib/manufacturing/qcChecklistOrder";
import { useEffect, useState } from "react";
import { BuilderAssistantChecklistItemEdit } from "./BuilderAssistantChecklistItemEdit";

const WALKING = new Set(["active", "tentative", "archive_requested"]);

function statusLabel(status: string): string | null {
  if (status === "tentative") return "Pending owner";
  if (status === "archive_requested") return "Removal requested";
  return null;
}

export function BuilderAssistantChecklistCard({
  category,
  items,
  isOwner,
  notesLocked,
  categoryIds,
  categories,
  mode,
  onRequestOpen,
  onConfirmOpen,
  onCancel,
  onClose,
  run,
}: {
  category: QcChecklistCategory;
  items: QcChecklistItem[];
  isOwner: boolean;
  notesLocked: boolean;
  categoryIds: string[];
  categories: QcChecklistCategory[];
  mode: "closed" | "confirm" | "open";
  onRequestOpen: () => void;
  onConfirmOpen: () => void;
  onCancel: () => void;
  onClose: () => void;
  run: (task: Promise<{ ok: boolean; error?: string }>) => void;
}) {
  const visible = items.filter((item) => item.category_id === category.id && WALKING.has(item.status));
  const itemIds = visible.map((item) => item.id);
  const [sectionName, setSectionName] = useState(category.name);
  useEffect(() => {
    setSectionName(category.name);
  }, [category.name]);

  function moveSection(direction: -1 | 1) {
    const ids = moveChecklistId(categoryIds, category.id, direction);
    if (ids) void run(reorderQcChecklist("category", ids));
  }

  function saveSectionName() {
    const name = sectionName.trim();
    if (!name || name === category.name) return;
    void run(renameQcChecklistCategory(category.id, name));
  }

  return (
    <article className={sectionCardClass}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-zinc-100">{category.name}</h4>
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            {visible.length} {visible.length === 1 ? "item" : "items"}
            {category.required ? " · Required" : " · Optional"}
            {statusLabel(category.status) ? ` · ${statusLabel(category.status)}` : ""}
          </p>
        </div>
        {mode === "open" ? (
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Save and close
          </button>
        ) : (
          <button type="button" className={ghostBtnClass} onClick={onRequestOpen}>
            Edit
          </button>
        )}
      </div>
      {mode === "confirm" ? (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950/40">
          <p className="text-gray-800 dark:text-zinc-200">Open {category.name} to edit this checklist?</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} onClick={onConfirmOpen}>
              Open
            </button>
            <button type="button" className={ghostBtnClass} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {mode === "open" ? (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} disabled={categoryIds[0] === category.id} onClick={() => moveSection(-1)}>
              Move section up
            </button>
            <button type="button" className={ghostBtnClass} disabled={categoryIds[categoryIds.length - 1] === category.id} onClick={() => moveSection(1)}>
              Move section down
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => void run(setQcCategoryRequired(category.id, !category.required))}
            >
              {category.required ? "Required" : "Optional"}
            </button>
            {category.status === "active" ? (
              <button type="button" className={ghostBtnClass} onClick={() => void run(requestQcChecklistArchive("category", category.id))}>
                Schedule removal
              </button>
            ) : null}
            {isOwner && category.status === "archive_requested" ? (
              <>
                <button type="button" className={ghostBtnClass} onClick={() => void run(ownerQcChecklistDecision({ action: "archive", kind: "category", id: category.id }))}>
                  Remove
                </button>
                <button type="button" className={ghostBtnClass} onClick={() => void run(ownerQcChecklistDecision({ action: "keep", kind: "category", id: category.id }))}>
                  Keep
                </button>
              </>
            ) : null}
          </div>
          <label className="block text-xs text-gray-600 dark:text-zinc-400">
            Section name
            <input
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900"
              value={sectionName}
              onChange={(event) => setSectionName(event.target.value)}
              onBlur={saveSectionName}
            />
          </label>
          <ul className="space-y-2">
            {visible.map((item) => (
              <BuilderAssistantChecklistItemEdit
                key={item.id}
                item={item}
                itemIds={itemIds}
                categories={categories}
                isOwner={isOwner}
                notesLocked={notesLocked}
                run={run}
              />
            ))}
          </ul>
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Save and close
          </button>
        </div>
      ) : null}
    </article>
  );
}
